import { Card } from '@/components/ui/card';
import { 
    Calendar, 
    Puzzle,
    MessageSquare,
    Phone,
    LayoutGrid,
    Globe,
    Webhook,
    Shield,
    MessageCircle,
    PhoneCall
} from 'lucide-react';
import type { WidgetResponse } from '@/api/widgets/widgets.types';
import { motion, type Variants } from 'framer-motion';
import { cn } from '@/lib/utils';
import { FadeId } from '@/components/ui/fade-id';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface WidgetCardProps {
    widget: WidgetResponse;
    onClick: () => void;
    variants?: Variants;
}

interface StatButtonProps {
    icon: React.ElementType;
    value: string | number;
    label: string;
    tooltip: string;
    active?: boolean;
}

const StatButton = ({ icon: Icon, value, label, tooltip, active = false }: StatButtonProps) => {
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            "h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2",
                            "hover:bg-muted/50 transition-colors cursor-default",
                            active && "bg-primary/5"
                        )}
                    >
                        <div className="flex items-center gap-1 mb-0.5">
                            <Icon className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-semibold">{value}</span>
                        </div>
                        <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="text-xs">{tooltip}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export const WidgetCard = ({ widget, onClick, variants }: WidgetCardProps) => {
    // Calculate stats
    const enabledCustomCards = widget.uiConfig?.customCards?.filter(card => card.enabled).length || 0;
    const allowedOrigins = widget.allowedOrigins?.length || 0;
    const hasChatEnabled = widget.chatEnabled ? 'Yes' : 'No';
    const hasVoiceEnabled = widget.voiceConfig?.enabled ? 'Yes' : 'No';
    const hasWebhook = widget.webhookUrl ? 'Yes' : 'No';
    const hasWhatsApp = widget.uiConfig?.showWhatsApp ? 'Yes' : 'No';
    const brandColor = widget.uiConfig?.brandColor || '#036ffc';

    return (
        <motion.div variants={variants}>
            <Card
                className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
                onClick={onClick}
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title, and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm">
                                    <Puzzle className="h-5 w-5 text-primary" />
                                </div>
                            </div>

                            {/* Title and Date */}
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-semibold tracking-tight truncate">
                                    {widget.name}
                                </h3>
                                {/* Date - handle both snake_case and camelCase */}
                                {(widget.updated_at || (widget as any).updatedAt) && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(widget.updated_at || (widget as any).updatedAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Chatbot ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId 
                                id={widget.chatbotId} 
                                label="Chatbot ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Unified Configuration Grid with Dividers */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 1 - Core Communication Features */}
                            <StatButton
                                icon={MessageSquare}
                                value={hasChatEnabled}
                                label="Chat"
                                tooltip={hasChatEnabled === 'Yes' ? 'Chat is enabled' : 'Chat is disabled'}
                                active={widget.chatEnabled}
                            />
                            
                            <StatButton
                                icon={Phone}
                                value={hasVoiceEnabled}
                                label="Voice"
                                tooltip={hasVoiceEnabled === 'Yes' ? 'Voice is enabled' : 'Voice is disabled'}
                                active={widget.voiceConfig?.enabled}
                            />

                            <StatButton
                                icon={MessageCircle}
                                value={hasWhatsApp}
                                label="WhatsApp"
                                tooltip={hasWhatsApp === 'Yes' ? 'WhatsApp option enabled' : 'WhatsApp option disabled'}
                                active={widget.uiConfig?.showWhatsApp}
                            />
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div className="border-t border-border" />
                        
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 2 - Configuration & Customization */}
                            <StatButton
                                icon={LayoutGrid}
                                value={enabledCustomCards}
                                label="Cards"
                                tooltip={`${enabledCustomCards} custom card${enabledCustomCards === 1 ? '' : 's'} enabled`}
                            />

                            <StatButton
                                icon={Globe}
                                value={allowedOrigins}
                                label="Origins"
                                tooltip={`${allowedOrigins} allowed origin${allowedOrigins === 1 ? '' : 's'}`}
                            />

                            {/* Brand Color - Custom Layout */}
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2 hover:bg-muted/50 transition-colors cursor-default">
                                            <div className="flex items-center gap-1.5 mb-0.5">
                                                <div 
                                                    className="h-3.5 w-3.5 rounded-full ring-1 ring-border shadow-sm"
                                                    style={{ backgroundColor: brandColor }}
                                                />
                                                <span className="text-xs font-semibold truncate max-w-[60px]">{brandColor}</span>
                                            </div>
                                            <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">Brand</span>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="text-xs">Brand color: {brandColor}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};

export const WidgetCardSkeleton = () => (
    <Card className="shadow-sm">
        <div className="p-5 space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm" />
                    <div className="flex-1 min-w-0 space-y-2">
                        <div className="h-5 w-3/4 bg-muted rounded animate-pulse" />
                        <div className="h-4 w-1/3 bg-muted rounded animate-pulse" />
                    </div>
                </div>
                <div className="h-8 w-36 bg-muted rounded-md animate-pulse" />
            </div>

            {/* Grid Skeleton */}
            <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-6 w-10 bg-muted rounded animate-pulse" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-6 w-10 bg-muted rounded animate-pulse" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-6 w-8 bg-muted rounded animate-pulse" />
                    </div>
                </div>
                <div className="border-t border-border" />
                <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-6 w-8 bg-muted rounded animate-pulse" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-6 w-16 bg-muted rounded animate-pulse" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    </div>
                </div>
            </div>
        </div>
    </Card>
);

